import React, { useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Alert, Button, Container, Input, Spinner } from 'reactstrap';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import {
  getAdminParameters,
  updateAdminParameter,
} from '../../../helpers/api/admin/parameter';
import { useAuthStore } from '../../../store/useAuthStore';
import type { ApplicationParameter } from '../../../types/api.generated';

const Parameter = () => {
  document.title = 'Admin Colosseum | Parameters';

  const { isAdmin, setApplicationParameters } = useAuthStore();
  const admin = isAdmin();
  const [parameters, setParameters] = useState<ApplicationParameter[]>([]);
  const [values, setValues] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!admin) return undefined;

    let active = true;

    const loadParameters = async () => {
      setLoading(true);
      setError(null);

      try {
        const nextParameters = await getAdminParameters();
        if (!active) return;

        setParameters(nextParameters);
        setValues(Object.fromEntries(nextParameters.map((parameter) => [parameter.code, parameter.value])));
        setApplicationParameters(nextParameters);
      } catch {
        if (active) {
          setError('Unable to load application parameters. Please try again.');
        }
      } finally {
        if (active) setLoading(false);
      }
    };

    loadParameters();

    return () => {
      active = false;
    };
  }, [admin, setApplicationParameters]);

  const modifiedParameters = useMemo(
    () => parameters.filter((parameter) => values[parameter.code] !== parameter.value),
    [parameters, values],
  );

  const handleSave = async () => {
    setSaving(true);

    const results = await Promise.all(modifiedParameters.map(async (parameter) => {
      try {
        const updatedParameter = await updateAdminParameter(parameter.code, {
          value: values[parameter.code],
        });
        toast.success(`${parameter.code} saved.`);
        return updatedParameter;
      } catch {
        toast.error(`Unable to save ${parameter.code}.`);
        return null;
      }
    }));

    const successfulParameters = results.filter(
      (parameter): parameter is ApplicationParameter => parameter !== null,
    );

    if (successfulParameters.length > 0) {
      const successfulByCode = new Map(
        successfulParameters.map((parameter) => [parameter.code, parameter]),
      );
      const nextParameters = parameters.map(
        (parameter) => successfulByCode.get(parameter.code) || parameter,
      );

      setParameters(nextParameters);
      setValues((currentValues) => ({
        ...currentValues,
        ...Object.fromEntries(
          successfulParameters.map((parameter) => [parameter.code, parameter.value]),
        ),
      }));
      setApplicationParameters(nextParameters);
    }

    setSaving(false);
  };

  if (!admin) {
    return <Navigate to={{ pathname: '/dashboard' }} />;
  }

  return (
    <div className="page-content admin-parameters-page" data-cy="admin-parameters-page">
      <Container fluid>
        <div className="admin-parameters-heading">
          <div>
            <h4 className="mb-1"><span className="gradient-text-brand">Parameters</span></h4>
            <p className="text-muted mb-0">Manage application-wide configuration values.</p>
          </div>
          <Button
            type="button"
            className="admin-parameters-save"
            disabled={loading || saving || modifiedParameters.length === 0}
            onClick={handleSave}
            data-cy="parameters-save"
          >
            {saving ? <><Spinner size="sm" className="me-2" />Saving...</> : 'Save'}
          </Button>
        </div>

        {error && <Alert color="danger" role="alert">{error}</Alert>}

        <section className="admin-parameters-panel" aria-label="Application parameters">
          {loading ? (
            <div className="admin-parameters-state" data-cy="parameters-loading">
              <Spinner size="sm" className="me-2" /> Loading parameters...
            </div>
          ) : parameters.length === 0 ? (
            <div className="admin-parameters-state" data-cy="parameters-empty">
              No application parameters found.
            </div>
          ) : (
            <div className="admin-parameters-list">
              {parameters.map((parameter) => {
                const inputId = `parameter-${parameter.code}`;
                const modified = values[parameter.code] !== parameter.value;

                return (
                  <div
                    className={`admin-parameters-row${modified ? ' admin-parameters-row--modified' : ''}`}
                    key={parameter.code}
                    data-cy="parameter-row"
                  >
                    <div className="admin-parameters-details">
                      <label htmlFor={inputId}>{parameter.code}</label>
                      <p>{parameter.description}</p>
                      <span>{parameter.type}</span>
                    </div>
                    <Input
                      id={inputId}
                      type="text"
                      value={values[parameter.code] ?? ''}
                      disabled={saving}
                      maxLength={127}
                      onChange={(event) => setValues((currentValues) => ({
                        ...currentValues,
                        [parameter.code]: event.target.value,
                      }))}
                      data-cy={`parameter-input-${parameter.code}`}
                    />
                  </div>
                );
              })}
            </div>
          )}
        </section>
      </Container>
      <ToastContainer autoClose={4000} position="top-right" hideProgressBar />
    </div>
  );
};

export default Parameter;
